import { FC, ReactNode, useMemo } from 'react';
import { twMerge } from 'tailwind-merge';

import { AppFormattedMessage } from '../../components/app-formatted-message/index';
import { Table, TableRowData } from '../../components/table/index';
import { selectedCompanyStorage } from '../../storage/company/selected-company.util';
import { StakeholderConsolidatedPositionShareClass } from '../../types/stakeholder.types';
import { toRound } from '../../utils/get-rounded-number.util';
import { SummaryItem } from './summary-item';

enum Column {
  CONVERTED_BASIS = 'converted-basis',
  CONVERTED_BASIS_PERCENT = 'converted-basis-percent',
  DILUTED = 'diluted',
  DILUTED_SHARES = 'diluted-shares',
  INVESTMENT = 'investment',
  ISSUED = 'issued',
  ISSUED_SHARES = 'issued-shares',
  SHARE_CLASS = 'share-class',
  VOTING = 'voting',
}

const tableColumnTitle: Record<Column, ReactNode> = {
  [Column.SHARE_CLASS]: <AppFormattedMessage id="SHARE_CLASS" />,
  [Column.ISSUED]: <AppFormattedMessage id="ISSUED_PERCENTAGE" />,
  [Column.DILUTED]: <AppFormattedMessage id="DILUTED_PERCENTAGE" />,
  [Column.VOTING]: <AppFormattedMessage id="VOTING" />,
  [Column.ISSUED_SHARES]: <AppFormattedMessage id="ISSUED_SHARES" />,
  [Column.DILUTED_SHARES]: <AppFormattedMessage id="DILUTED_SHARES" />,
  [Column.INVESTMENT]: <AppFormattedMessage id="INVESTMENT" />,
  [Column.CONVERTED_BASIS]: <AppFormattedMessage id="CONVERTED_BASIS" />,
  [Column.CONVERTED_BASIS_PERCENT]: <AppFormattedMessage id="CONVERTED_BASIS_PERCENT" />,
};

const tableColumn: Column[] = Object.keys(tableColumnTitle);

export type ConsolidatedPositionProps = {
  shareClasses?: StakeholderConsolidatedPositionShareClass[];
};

export const ConsolidatedPosition: FC<ConsolidatedPositionProps> = ({ shareClasses }) => {
  const company = selectedCompanyStorage(({ company }) => company);
  const currency = company?.currency;

  const stakeholderShareClasses = useMemo(() => shareClasses ?? [], [shareClasses]);

  const isConsolidatedPositionEmpty = !stakeholderShareClasses.length;

  const tableData: TableRowData<Column>[] = useMemo(() => {
    if (isConsolidatedPositionEmpty)
      return [
        {
          [Column.SHARE_CLASS]: 'Common Stock',
          [Column.ISSUED]: '100',
          [Column.DILUTED]: '100',
          [Column.VOTING]: '100',
          [Column.ISSUED_SHARES]: '1,000',
          [Column.DILUTED_SHARES]: '1,000',
          [Column.INVESTMENT]: '؋0',
          id: '0',
          [Column.CONVERTED_BASIS]: '1,000',
          [Column.CONVERTED_BASIS_PERCENT]: '100',
        },
        {
          [Column.SHARE_CLASS]: 'Common Stock 2',
          [Column.ISSUED]: '100',
          [Column.DILUTED]: '100',
          [Column.VOTING]: '100',
          [Column.ISSUED_SHARES]: '1,000',
          [Column.DILUTED_SHARES]: '1,000',
          [Column.INVESTMENT]: '؋0',
          id: '1',
          [Column.CONVERTED_BASIS]: '1,000',
          [Column.CONVERTED_BASIS_PERCENT]: '100',
        },
      ];

    return stakeholderShareClasses.map(
      ({
        name,
        id,
        diluted,
        dilutedPercentage,
        investment,
        issued,
        issuedPercentage,
        votingPercentage,
        converted,
        convertedPercentage,
      }) => ({
        [Column.SHARE_CLASS]: name,
        [Column.ISSUED]: `${toRound(issuedPercentage * 100)}`,
        [Column.DILUTED]: `${toRound(dilutedPercentage * 100)}`,
        [Column.VOTING]: `${toRound(votingPercentage * 100)}`,
        [Column.ISSUED_SHARES]: `${issued.toLocaleString('en-US')}`,
        [Column.DILUTED_SHARES]: `${diluted.toLocaleString('en-US')}`,
        [Column.INVESTMENT]: `${currency?.symbol || currency?.iso3?.toUpperCase()}${investment.toLocaleString('en-US')}`,
        id,
        [Column.CONVERTED_BASIS]: converted.toLocaleString('en-US'),
        [Column.CONVERTED_BASIS_PERCENT]: `${toRound(convertedPercentage * 100)}`,
      }),
    );
  }, [currency?.iso3, currency?.symbol, isConsolidatedPositionEmpty, stakeholderShareClasses]);

  return (
    <>
      <div className="flex flex-grow flex-col gap-3 overflow-hidden rounded-lg bg-gray-50 p-[2px] pt-3 max-lg:hidden lg:basis-[60%]">
        <span className="pl-4 text-[10px] font-bold uppercase leading-4 tracking-[1px] text-gray-700">
          <AppFormattedMessage id="YOUR_CONSOLIDATED_POSITION" />
        </span>
        <div
          className={twMerge(
            'centered relative flex h-full w-full flex-wrap gap-3 rounded-md bg-white p-1 shadow-sm',
          )}
        >
          <div
            className={twMerge(
              'h-full w-full overflow-x-auto rounded-md border border-gray-100',
              isConsolidatedPositionEmpty && 'select-none opacity-50 blur-md',
            )}
          >
            <Table columnOrder={tableColumn} columnsTitle={tableColumnTitle} rows={tableData} />
          </div>
          {isConsolidatedPositionEmpty && (
            <span className="absolute z-1 text-label-md font-medium text-gray-500">
              <AppFormattedMessage id="YOUR_CONSOLIDATED_POSITION_WILL_BE_HERE" />
            </span>
          )}
        </div>
      </div>

      <div className="flex w-full flex-col gap-3 rounded-lg bg-gray-50 p-1 pt-3 lg:hidden">
        <span className="px-4 text-[10px] font-bold uppercase leading-4 tracking-[1px] text-gray-700">
          <AppFormattedMessage id="YOUR_CONSOLIDATED_POSITION" />
        </span>
        <div className="centered relative flex h-full w-full">
          {isConsolidatedPositionEmpty && (
            <span className="absolute z-1 text-label-md font-medium text-gray-500">
              <AppFormattedMessage id="YOUR_CONSOLIDATED_POSITION_WILL_BE_HERE" />
            </span>
          )}
          <div
            className={twMerge(
              'flex h-full w-full flex-col gap-2',
              isConsolidatedPositionEmpty && 'h-full select-none opacity-50 blur-md',
            )}
          >
            {tableData.map((data) => (
              <div
                className="flex w-full flex-col divide-y divide-gray-100 overflow-hidden rounded-md bg-white shadow-sm"
                key={data.id}
              >
                {tableColumn.map((column) => (
                  <SummaryItem key={column} title={tableColumnTitle[column]} value={data[column]} />
                ))}
              </div>
            ))}
          </div>
        </div>
      </div>
    </>
  );
};
